import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  return (
    <>
      <SEO
        title="Privacy Policy - ANIMAC Entertainment Hub"
        description="ANIMAC Privacy Policy - Learn how we collect, use, and protect your personal information."
        url="/privacy"
        type="website"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white">
        {/* Hero Section */}
        <section className="relative py-20 md:py-32 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1557683316-973673baf926?w=1920&q=80" 
              alt="Privacy Policy" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/80 to-gray-900/90" />
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-6xl font-azonix font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Privacy Policy
              </h1>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Your privacy is important to us. This policy outlines how we collect, use, and protect your information.
              </p>
              <p className="text-gray-500 text-sm mt-4">Last updated: March 2025</p>
            </motion.div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-12 md:py-20">
          <div className="container mx-auto px-4 max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="prose prose-invert prose-lg max-w-none"
            >
              <div className="space-y-8">
                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">1. Information We Collect</h2>
                  <p className="text-gray-300 leading-relaxed">
                    We collect information you provide directly to us, including but not limited to:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 mt-4 space-y-2">
                    <li>Account information (username, email, profile data)</li>
                    <li>Content you submit (comments, reviews, articles)</li>
                    <li>Communication data (newsletter subscriptions, contact forms)</li>
                    <li>Usage data (how you interact with our platform)</li>
                  </ul>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">2. How We Use Your Information</h2>
                  <p className="text-gray-300 leading-relaxed">
                    We use the information we collect to:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 mt-4 space-y-2">
                    <li>Provide, maintain, and improve our services</li>
                    <li>Send you newsletters and updates about our content</li>
                    <li>Respond to your comments, questions, and requests</li>
                    <li>Analyze usage patterns to enhance user experience</li>
                    <li>Protect against fraud and abuse</li>
                  </ul>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">3. Information Sharing</h2>
                  <p className="text-gray-300 leading-relaxed">
                    We do not sell, trade, or otherwise transfer your personal information to outside parties. We may share information with:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 mt-4 space-y-2">
                    <li>Service providers who assist in our operations</li>
                    <li>Legal authorities when required by law</li>
                    <li>Business partners with your consent</li>
                  </ul>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">4. Cookies and Tracking</h2>
                  <p className="text-gray-300 leading-relaxed">
                    We use cookies and similar tracking technologies to track the activity on our service and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">5. Data Security</h2>
                  <p className="text-gray-300 leading-relaxed">
                    We implement appropriate security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">6. Third-Party Links</h2>
                  <p className="text-gray-300 leading-relaxed">
                    Our service may contain links to third-party websites. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">7. Children's Privacy</h2>
                  <p className="text-gray-300 leading-relaxed">
                    Our service does not address anyone under the age of 13. We do not knowingly collect personal information from children under 13.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">8. Your Rights</h2>
                  <p className="text-gray-300 leading-relaxed">
                    You have the right to access, update, or delete your personal information. Contact us at iconanimac@gmail.com to exercise these rights.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">9. Changes to This Policy</h2>
                  <p className="text-gray-300 leading-relaxed">
                    We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">10. Contact Us</h2>
                  <p className="text-gray-300 leading-relaxed">
                    If you have any questions about this Privacy Policy, please contact us at iconanimac@gmail.com.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PrivacyPolicy;